import type { VideoAbVariant } from "@/lib/useVideoAbSlot";
import { EDITORIAL_HOME_NO_CTA_COUNTRIES } from "@/lib/editorialHomeNoCtaLock";

export const DUAL_CTA_HOME_SLOT_KEY = "dual_cta_landing";

export type VideoAbSlotAdmin = {
  id?: number;
  slot_key: string;
  label: string;
  variants: VideoAbVariant[];
  selection_mode: "weighted_random" | "first";
  enabled: boolean;
  notes?: string | null;
  updated_at?: string;
};

export type KnownMembershipPrice = {
  id: string;
  label: string;
  tier: string;
  /** Monto USD/mes para preview de descuento primer mes. */
  amountUsd?: number;
};

export function emptyVideoAbVariant(weight = 50): VideoAbVariant {
  return { playback_id: "", label: "", weight, enabled: true, countries: [] };
}

export function defaultDualCtaHomeSlot(): VideoAbSlotAdmin {
  return {
    slot_key: DUAL_CTA_HOME_SLOT_KEY,
    label: "DualCTA Video Landing (home mundial)",
    variants: [
      { ...emptyVideoAbVariant(50), label: "Video A" },
      { ...emptyVideoAbVariant(50), label: "Video B" },
    ],
    selection_mode: "weighted_random",
    enabled: true,
    notes: "Home mundial — variantes Mux + precio membresía por país.",
  };
}

export function totalVariantWeight(variants: VideoAbVariant[]): number {
  return variants.filter((v) => v.enabled).reduce((s, v) => s + (v.weight ?? 0), 0);
}

/** Variante lista para servir en la home (playback + enabled). */
export function hasPublishableVideoAbVariants(variants: VideoAbVariant[]): boolean {
  return variants.some(
    (v) => v.enabled && String(v.playback_id || "").trim().length >= 10,
  );
}

/**
 * Si hay variantes con playback pero el slot está off, activarlo al guardar desde
 * /admin/dual-cta-home — evita que el visitante ignore el A/B y caiga a fallback.
 */
export function coerceSlotEnabledForPublish(slot: VideoAbSlotAdmin): VideoAbSlotAdmin {
  if (slot.enabled || !hasPublishableVideoAbVariants(slot.variants)) return slot;
  return { ...slot, enabled: true };
}

export type VideoAbCoherenceIssue =
  | { kind: "slot_off_with_variants"; message: string }
  | {
      kind: "identical_playback";
      /** Info, not a hard block: same Mux is a valid workaround for price/label/weight A/B. */
      severity: "info";
      message: string;
      playbackId: string;
      labels: string[];
    }
  | {
      kind: "editorial_lock_in_scope";
      severity: "warn";
      message: string;
      countries: string[];
    }
  | {
      kind: "weight_sum_not_100";
      severity: "warn";
      message: string;
      total: number;
    };

/** Resumen operativo del slot para el admin (gestión / cobertura A/B). */
export type VideoAbSlotFootprint = {
  slotEnabled: boolean;
  selectionMode: "weighted_random" | "first";
  publishableCount: number;
  distinctPlaybackCount: number;
  distinctPlaybacks: Array<{
    playback_id: string;
    labels: string[];
    weightSum: number;
  }>;
  /** ≥2 playback IDs distintos entre variantes publicables. */
  isMultiMux: boolean;
  /** Todas las publicables tienen countries[] no vacío. */
  isCountryScoped: boolean;
  /** Alguna publicable sin filtro de país (= global). */
  isGlobal: boolean;
  /** Unión de ISO2 en countries[] de variantes publicables (vacío si global). */
  countriesInScope: string[];
  countryCount: number;
  /** Países con ≥2 variantes elegibles (mismo ISO o global). */
  countriesWithMultiVariant: string[];
  multiVariantCountryCount: number;
  /** Países del scope que no ven DualCTA home (Base editorial sin CTA). */
  editorialLockedInScope: string[];
  weightTotal: number;
  weightOk: boolean;
  mode: "multi_mux_ab" | "same_mux_offer_ab" | "single" | "empty" | "off";
};

function shortPlaybackId(pid: string, n = 12): string {
  const t = pid.trim();
  return t.length > n + 1 ? `${t.slice(0, n)}…` : t;
}

/**
 * Footprint del slot dual_cta_landing: qué videos, a cuántos países, si hay multi-Mux real.
 * Fuente de verdad para el panel de gestión en /admin/dual-cta-home.
 */
export function summarizeVideoAbSlotFootprint(slot: VideoAbSlotAdmin): VideoAbSlotFootprint {
  const publishable = (slot.variants || []).filter(
    (v) => v.enabled && String(v.playback_id || "").trim().length >= 10,
  );
  const weightTotal = publishable.reduce((s, v) => s + (Number(v.weight) || 0), 0);
  const weightOk =
    weightTotal === 100 ||
    slot.selection_mode === "first" ||
    publishable.length <= 1;

  const byPlayback = new Map<string, { labels: string[]; weightSum: number }>();
  for (const v of publishable) {
    const pid = String(v.playback_id || "").trim();
    const cur = byPlayback.get(pid) || { labels: [], weightSum: 0 };
    cur.labels.push(v.label?.trim() || "(sin label)");
    cur.weightSum += Number(v.weight) || 0;
    byPlayback.set(pid, cur);
  }
  const distinctPlaybacks = [...byPlayback.entries()].map(([playback_id, meta]) => ({
    playback_id,
    labels: meta.labels,
    weightSum: meta.weightSum,
  }));

  const isMultiMux = distinctPlaybacks.length >= 2;
  const isGlobal = publishable.some((v) => !v.countries?.length);
  const isCountryScoped =
    publishable.length > 0 && publishable.every((v) => (v.countries?.length ?? 0) > 0);

  const countrySet = new Set<string>();
  for (const v of publishable) {
    for (const c of v.countries || []) {
      const u = String(c).trim().toUpperCase().slice(0, 2);
      if (/^[A-Z]{2}$/.test(u)) countrySet.add(u);
    }
  }
  const countriesInScope = [...countrySet].sort();

  // Multi-variant countries: ISO with ≥2 eligible variants, or global multi-variant (all DualCTA ISOs).
  const multiCountries = new Set<string>();
  if (isGlobal && publishable.length >= 2) {
    // Global multi = applies everywhere DualCTA runs; mark as "*" for summary only.
    multiCountries.add("*");
  } else if (isCountryScoped) {
    for (const code of countriesInScope) {
      const hits = publishable.filter((v) =>
        (v.countries || []).some((c) => c.toUpperCase() === code),
      );
      if (hits.length >= 2) multiCountries.add(code);
    }
  }
  const countriesWithMultiVariant = [...multiCountries].filter((c) => c !== "*").sort();

  const editorialLockedInScope = countriesInScope.filter((c) =>
    EDITORIAL_HOME_NO_CTA_COUNTRIES.has(c),
  );

  let mode: VideoAbSlotFootprint["mode"] = "empty";
  if (!slot.enabled && publishable.length > 0) mode = "off";
  else if (publishable.length === 0) mode = "empty";
  else if (publishable.length === 1) mode = "single";
  else if (isMultiMux) mode = "multi_mux_ab";
  else mode = "same_mux_offer_ab";

  return {
    slotEnabled: !!slot.enabled,
    selectionMode: slot.selection_mode,
    publishableCount: publishable.length,
    distinctPlaybackCount: distinctPlaybacks.length,
    distinctPlaybacks,
    isMultiMux,
    isCountryScoped,
    isGlobal,
    countriesInScope,
    countryCount: countriesInScope.length,
    countriesWithMultiVariant,
    multiVariantCountryCount:
      multiCountries.has("*") ? countriesInScope.length || -1 : countriesWithMultiVariant.length,
    editorialLockedInScope,
    weightTotal,
    weightOk,
    mode,
  };
}

/**
 * Issues de coherencia del slot (no bloquean el save).
 * identical_playback = aviso: mismo Mux no prueba creatividades distintas,
 * pero SÍ sirve para A/B real de precio / label / cupón / peso.
 */
export function detectVideoAbCoherenceIssues(slot: VideoAbSlotAdmin): VideoAbCoherenceIssue[] {
  const issues: VideoAbCoherenceIssue[] = [];
  const publishable = (slot.variants || []).filter(
    (v) => v.enabled && String(v.playback_id || "").trim().length >= 10,
  );
  const fp = summarizeVideoAbSlotFootprint(slot);

  if (!slot.enabled && publishable.length > 0) {
    issues.push({
      kind: "slot_off_with_variants",
      message:
        "Slot A/B OFF con variantes publicables — el visitante usa baseline (country dualCta / env), no A/B.",
    });
  }

  const byPlayback = new Map<string, string[]>();
  for (const v of publishable) {
    const pid = String(v.playback_id || "").trim();
    const labels = byPlayback.get(pid) || [];
    labels.push(v.label?.trim() || "(sin label)");
    byPlayback.set(pid, labels);
  }
  for (const [pid, labels] of byPlayback) {
    if (labels.length < 2) continue;
    issues.push({
      kind: "identical_playback",
      severity: "info",
      message: `Variantes ${labels.slice(0, 4).join(" y ")} comparten el mismo Mux (${shortPlaybackId(pid)}). OK como workaround: medís precio/label/peso/cupón, no creatividades distintas.`,
      playbackId: pid,
      labels,
    });
  }

  if (
    slot.enabled &&
    slot.selection_mode === "weighted_random" &&
    publishable.length >= 2 &&
    !fp.weightOk
  ) {
    issues.push({
      kind: "weight_sum_not_100",
      severity: "warn",
      message: `Pesos de variantes activas suman ${fp.weightTotal}% (deben sumar 100 en modo aleatorio ponderado).`,
      total: fp.weightTotal,
    });
  }

  if (fp.editorialLockedInScope.length > 0 && slot.enabled && publishable.length > 0) {
    const sample = fp.editorialLockedInScope.slice(0, 8).join(", ");
    const extra =
      fp.editorialLockedInScope.length > 8
        ? ` +${fp.editorialLockedInScope.length - 8}`
        : "";
    issues.push({
      kind: "editorial_lock_in_scope",
      severity: "warn",
      message: `El slot A/B incluye ${fp.editorialLockedInScope.length} país(es) con home editorial sin DualCTA (${sample}${extra}). Ahí el visitante no ve esta rotación de video en home — solo DualCTA (no-AR y no-lock).`,
      countries: fp.editorialLockedInScope,
    });
  }

  return issues;
}

/**
 * Duplica una variante con el MISMO playback_id (A/B de precio/label/peso).
 * Pesos se rebalancean a partes iguales entre enabled al final (caller puede ajustar).
 */
export function cloneVariantSamePlayback(
  source: VideoAbVariant,
  nextIndex: number,
  overrides?: Partial<VideoAbVariant>,
): VideoAbVariant {
  const letter = String.fromCharCode(65 + Math.max(0, nextIndex));
  const baseLabel = source.label?.trim() || `Video ${letter}`;
  return {
    ...source,
    playback_id: String(source.playback_id || "").trim(),
    label: `${baseLabel} · B${letter}`,
    weight: source.weight ?? 50,
    enabled: true,
    countries: source.countries ? [...source.countries] : [],
    // Copy offer fields so operator can then change price on the clone
    stripe_price_id: source.stripe_price_id,
    vip_label: source.vip_label,
    full_price_usd: source.full_price_usd,
    first_month_discount_percent: source.first_month_discount_percent,
    vip_video_asset_id: source.vip_video_asset_id,
    ...overrides,
  };
}

/**
 * Asegura ≥2 variantes publicables con el mismo playback (workaround A/B real).
 * - Si hay una con playback: clona a N (default 2) con pesos 50/50 (o igual reparto).
 * - Si hay playback explícito: rellena vacías o crea pares.
 * No desactiva el slot; el caller puede coerceSlotEnabledForPublish al guardar.
 */
export function buildSamePlaybackTestVariants(
  variants: VideoAbVariant[],
  opts?: { count?: number; playbackId?: string; force?: boolean },
): VideoAbVariant[] {
  const want = Math.max(2, Math.min(6, opts?.count ?? 2));
  const forcedPid = opts?.playbackId?.trim() || "";
  const list = [...variants];

  const withPid = list.filter((v) => String(v.playback_id || "").trim().length >= 10);
  const source =
    (forcedPid
      ? list.find((v) => String(v.playback_id || "").trim() === forcedPid)
      : undefined) ||
    withPid[0] ||
    list[0];

  const pid =
    forcedPid ||
    String(source?.playback_id || "").trim() ||
    "";

  if (pid.length < 10) {
    // Nothing to clone from — return as-is (UI should toast)
    return list;
  }

  // Seed first variant if empty
  if (list.length === 0) {
    list.push({
      ...emptyVideoAbVariant(Math.floor(100 / want)),
      playback_id: pid,
      label: "Prueba A · mismo Mux",
      enabled: true,
    });
  } else if (!String(list[0].playback_id || "").trim()) {
    list[0] = {
      ...list[0],
      playback_id: pid,
      label: list[0].label?.trim() || "Prueba A · mismo Mux",
      enabled: true,
    };
  }

  // Ensure all existing empty rows get the same pid when force
  if (opts?.force) {
    for (let i = 0; i < list.length; i++) {
      if (!String(list[i].playback_id || "").trim()) {
        list[i] = {
          ...list[i],
          playback_id: pid,
          enabled: true,
          label: list[i].label?.trim() || `Prueba ${String.fromCharCode(65 + i)} · mismo Mux`,
        };
      }
    }
  }

  while (list.length < want) {
    const src = list[0];
    list.push(
      cloneVariantSamePlayback(src, list.length, {
        playback_id: pid,
        label: `Prueba ${String.fromCharCode(65 + list.length)} · mismo Mux`,
      }),
    );
  }

  // If we already have enough but they don't share pid, set enabled ones to same pid when force
  if (opts?.force) {
    for (let i = 0; i < Math.min(want, list.length); i++) {
      list[i] = {
        ...list[i],
        playback_id: pid,
        enabled: true,
      };
    }
  }

  // Rebalance weights among first `want` enabled clones
  const weightEach = Math.floor(100 / want);
  let rem = 100 - weightEach * want;
  for (let i = 0; i < want && i < list.length; i++) {
    list[i] = {
      ...list[i],
      weight: weightEach + (rem > 0 ? 1 : 0),
      enabled: true,
      playback_id: pid,
    };
    if (rem > 0) rem -= 1;
  }

  return list;
}

export function slotToSnapshot(s: VideoAbSlotAdmin): string {
  return JSON.stringify({
    slot_key: s.slot_key,
    label: s.label,
    variants: s.variants,
    selection_mode: s.selection_mode,
    enabled: s.enabled,
    notes: s.notes ?? null,
  });
}

/** Snapshot de la asignación A/B de un ISO (para dirty por país en dual-cta-home). */
export function countrySlotAssignmentSnapshot(
  variants: VideoAbVariant[],
  code: string,
): string {
  const upper = code.toUpperCase();
  if (upper === "DEFAULT" || upper === "AR") return "";
  const hits = variants
    .map((v, i) => ({ v, i }))
    .filter(({ v }) => (v.countries || []).some((c) => c.toUpperCase() === upper))
    .map(({ v, i }) => ({
      i,
      playback_id: String(v.playback_id || "").trim(),
      enabled: !!v.enabled,
      weight: v.weight ?? 0,
      stripe_price_id: String(v.stripe_price_id || "").trim(),
      vip_label: String(v.vip_label || "").trim(),
      full_price_usd: v.full_price_usd ?? null,
      first_month_discount_percent: v.first_month_discount_percent ?? null,
    }));
  return JSON.stringify(hits);
}

/** Meta global del slot (enabled/mode) — no por país. */
export function slotMetaSnapshot(s: Pick<VideoAbSlotAdmin, "enabled" | "selection_mode">): string {
  return JSON.stringify({ enabled: s.enabled, selection_mode: s.selection_mode });
}

export function countryFlagEmoji(code: string): string {
  if (code.length !== 2) return "🌍";
  return Array.from(code.toUpperCase())
    .map((c) => String.fromCodePoint(0x1f1e6 + c.charCodeAt(0) - 65))
    .join("");
}